<template>
  <button
    class="base-button"
    :class="buttonClass"
    :disabled="disabled || loading"
    @click="handleClick"
  >
    <span v-if="loading" class="base-button-spinner"></span>
    <vue-feather v-else-if="icon" :type="icon" size="18" class="base-button-icon" />
    <span v-if="label && !loading" class="base-button-label">{{ label }}</span>
  </button>
</template>

<script>
import VueFeather from 'vue-feather'

export default {
  name: 'BaseButton',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    label: {
      type: String,
      default: ''
    },
    // Variant: 'default', 'primary', 'danger', 'danger-outline', 'warning', 'success'
    variant: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'primary', 'danger', 'danger-outline', 'warning', 'success'].includes(value)
    },
    // Feather icon name (e.g., 'plus', 'edit', 'trash-2')
    icon: {
      type: String,
      default: ''
    },
    // Size: 'default', 'small'
    size: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'small'].includes(value)
    },
    // Full width (for modal buttons)
    block: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    buttonClass() {
      return {
        [`base-button--${this.variant}`]: this.variant !== 'default',
        'base-button--small': this.size === 'small',
        'base-button--icon-only': this.icon && !this.label,
        'base-button--block': this.block,
        'base-button--loading': this.loading
      }
    }
  },
  methods: {
    handleClick(event) {
      if (!this.disabled && !this.loading) {
        this.$emit('click', event)
      }
    }
  },
  emits: ['click']
}
</script>

<style scoped>
.base-button {
  min-height: 45px;
  font-size: 1rem;
  font-weight: 500;
  padding: 0 18px;
  border-radius: 4px;
  background: #FFF;
  border: 1px solid #d1d5db;
  outline: none;
  cursor: pointer;
  color: #374151;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.base-button:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}

.base-button:active:not(:disabled) {
  transform: scale(0.98);
}

/* Small */
.base-button--small {
  min-height: 32px;
  font-size: 0.8125rem;
  padding: 0 12px;
}

.base-button--small.base-button--icon-only {
  min-width: 32px;
}

/* Block (full width - for modals) */
.base-button--block {
  width: 100%;
  min-height: 50px;
  font-size: 0.9375rem;
}

/* Variants */
.base-button--primary {
  background: var(--color-primary, #6a16f0);
  color: #FFF;
  border: none;
  box-shadow: 0 1px 2px rgba(106, 22, 240, 0.15);
}

.base-button--primary:hover:not(:disabled) {
  background: #5c11d5;
  box-shadow: 0 2px 4px rgba(106, 22, 240, 0.25);
}

.base-button--danger {
  background: #dc2626;
  color: #FFF;
  border: none;
  box-shadow: 0 1px 2px rgba(220, 38, 38, 0.15);
}

.base-button--danger:hover:not(:disabled) {
  background: #b91c1c;
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.25);
}

.base-button--danger-outline {
  background: #FFF;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.base-button--danger-outline:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fca5a5;
}

.base-button--warning {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.base-button--warning:hover:not(:disabled) {
  background: #fef3c7;
  border-color: #fcd34d;
}

.base-button--success {
  background: #f0fdf4;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.base-button--success:hover:not(:disabled) {
  background: #dcfce7;
  border-color: #86efac;
}

/* Icon only */
.base-button--icon-only {
  min-width: 45px;
  padding: 0;
}

/* States */
.base-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

.base-button--loading {
  cursor: wait;
}

/* Icon */
.base-button-icon {
  flex-shrink: 0;
}

/* Loading spinner */
.base-button-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.base-button-label {
  white-space: nowrap;
}
</style>
